import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

const WHATSAPP_NUMBER = '91XXXXXXXXXX';

function buildWhatsAppMessage(items: ReturnType<typeof useCart>['items'], customerInfo: ReturnType<typeof useCart>['customerInfo'], subtotal: number): string {
  const header = `🐾 *New Order from PounceBounce!*\n\n`;

  let customerSection = '';
  if (customerInfo.name || customerInfo.phone) {
    customerSection = `*Customer Details:*\n`;
    if (customerInfo.name) customerSection += `👤 Name: ${customerInfo.name}\n`;
    if (customerInfo.phone) customerSection += `📱 Phone: ${customerInfo.phone}\n`;
    if (customerInfo.address) customerSection += `📍 Address: ${customerInfo.address}\n`;
    if (customerInfo.city) customerSection += `🏙️ City: ${customerInfo.city}`;
    if (customerInfo.pincode) customerSection += ` - ${customerInfo.pincode}`;
    customerSection += `\n\n`;
  }

  const itemsSection = `*Order Items:*\n${items.map((item, i) => {
    const price = item.variantPrice ?? item.product.price;
    const line = `${i + 1}. ${item.product.name}`;
    const variant = item.variantLabel ? ` (${item.variantLabel})` : '';
    return `${line}${variant} x${item.quantity} — Rs.${price * item.quantity}`;
  }).join('\n')}\n\n`;

  const totalSection = `*Subtotal: Rs.${subtotal}*\n\n`;

  let notesSection = '';
  if (customerInfo.orderNotes) {
    notesSection = `*Order Notes:*\n${customerInfo.orderNotes}\n\n`;
  }

  const footer = `_Please confirm my order! 🐾_`;

  return header + customerSection + itemsSection + totalSection + notesSection + footer;
}

export default function CartDrawer() {
  const { items, customerInfo, subtotal, removeItem, updateQuantity, isCartOpen, closeCart } = useCart();
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  if (!isCartOpen) return null;

  const handleWhatsAppCheckout = () => {
    const message = buildWhatsAppMessage(items, customerInfo, subtotal);
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-foreground-950/40 z-50 transition-opacity duration-300"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-background-50 z-50 flex flex-col animate-slide-in-right shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <h2 className="font-heading text-lg font-bold text-foreground-800 flex items-center gap-2">
            <i className="ri-shopping-bag-line text-primary-500"></i>
            Your Cart
            {items.length > 0 && (
              <span className="text-sm font-normal text-foreground-500">({items.reduce((s, i) => s + i.quantity, 0)} items)</span>
            )}
          </h2>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500"
            aria-label="Close cart"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Empty cart */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 px-6 py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center mb-5">
              <i className="ri-footprint-line text-3xl text-accent-500/60"></i>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">Your Cart is Resting</h3>
            <p className="text-foreground-500 text-sm mb-6 max-w-xs">
              Looks a little empty — pounce on something your pet will love!
            </p>
            <Link
              to="/shop"
              onClick={closeCart}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200"
            >
              Continue Shopping
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        ) : (
          <>
            {/* Line items */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map(item => {
                const price = item.variantPrice ?? item.product.price;
                return (
                  <div key={item.id} className="flex gap-3 pb-4 border-b border-background-200/50">
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-secondary-100">
                      <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground-800 truncate">{item.product.name}</h4>
                      {item.variantLabel && (
                        <p className="text-xs text-foreground-500 mt-0.5">{item.variantLabel}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-6 h-6 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 text-xs"
                            aria-label="Decrease quantity"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-7 text-center text-sm font-medium text-foreground-800">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-6 h-6 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 text-xs"
                            aria-label="Increase quantity"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                        <span className="text-sm font-bold text-foreground-800">Rs.{price * item.quantity}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-accent-500 hover:bg-accent-50 transition-colors duration-150 flex-shrink-0 mt-1"
                      aria-label={`Remove ${item.product.name}`}
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-background-200/70 px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-600">Subtotal</span>
                <span className="text-lg font-bold text-foreground-800">Rs.{subtotal}</span>
              </div>
              <p className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Final amount will be confirmed over WhatsApp
              </p>

              {/* Customer info toggle */}
              <button
                onClick={() => setShowCustomerForm(!showCustomerForm)}
                className="w-full flex items-center justify-between text-xs text-foreground-600 hover:text-primary-500 py-1"
              >
                <span className="flex items-center gap-1">
                  <i className="ri-user-settings-line"></i>
                  Add delivery details (optional)
                </span>
                <i className={`transition-transform duration-200 ${showCustomerForm ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {/* Checkout CTA */}
              <button
                onClick={handleWhatsAppCheckout}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm transition-all duration-200 paw-bounce bg-[#25D366] text-white hover:bg-[#22c35e]"
              >
                <i className="ri-whatsapp-fill text-lg paw-icon"></i>
                Checkout via WhatsApp
              </button>

              <Link
                to="/cart"
                onClick={closeCart}
                className="block w-full text-center text-xs text-foreground-500 hover:text-primary-500 transition-colors py-1"
              >
                View Full Cart
              </Link>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}